/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import { useNavigation } from 'react-router';
import cx from 'classnames';

import styles from './LoadingProgress.module.scss';

interface LoadingProgressProps {
    isHub?: boolean;
}

export function LoadingProgress({ isHub }: LoadingProgressProps) {
    const navigation = useNavigation();
    const isActive = navigation.state !== 'idle';

    const ref = React.useRef<HTMLDivElement>(null);
    const [animationComplete, setAnimationComplete] = React.useState(true);
    const [isSlowLoading, setSlowLoading] = React.useState(false);

    React.useEffect(() => {
        if (!ref.current) return;
        if (isActive) setAnimationComplete(false);

        Promise.allSettled(
            ref.current.getAnimations().map(({ finished }) => finished)
        ).then(() => !isActive && setAnimationComplete(true));
    }, [isActive]);

    // For pages with slow loaders we don't want to go too long without moving the progress bar,
    // so we do a little bump if the loading state stays around longer than 800ms
    React.useEffect(() => {
        let timeout: number = 0;

        if (navigation.state === 'loading') {
            timeout = window.setTimeout(() => {
                if (navigation.state === 'loading') {
                    setSlowLoading(true);
                }
            }, 800);
        }

        return () => {
            window.clearTimeout(timeout);
            setSlowLoading(false);
        };
    }, [navigation.state]);

    return (
        <div
            role="progressbar"
            aria-hidden={!isActive}
            aria-valuetext={isActive ? 'Loading' : undefined}
            className={styles.LoadingProgress}
        >
            <div
                ref={ref}
                className={cx(styles.bar, {
                    [styles.isHub]: isHub,
                    [styles.idle]: navigation.state === 'idle' && animationComplete,
                    [styles.submitting]: navigation.state === 'submitting',
                    [styles.loading]: navigation.state === 'loading' && !isSlowLoading,
                    [styles.loading2]: navigation.state === 'loading' && isSlowLoading,
                    [styles.complete]: navigation.state === 'idle' && !animationComplete,
                })}
            />
        </div>
    );
}
